import React, { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '../../supabaseClient';

interface SMSVerificationProps {
  phone: string;
  onVerified: (deviceToken: string) => void;
  onCancel: () => void;
}

const TOTAL_SECONDS = 180;
const RESEND_COOLDOWN = 60;
const CODE_LENGTH = 6;

const SMSVerification: React.FC<SMSVerificationProps> = ({ phone, onVerified, onCancel }) => {
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const maskedPhone = phone.length >= 4
    ? '••••••' + phone.slice(-4)
    : phone;

  // Countdown timer
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  // Resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const formatTime = (s: number) => {
    const min = Math.floor(s / 60);
    const sec = s % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  const submitCode = useCallback(async (code: string) => {
    setVerifying(true);
    setError(null);
    try {
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-phone-check', {
        body: { phone, code },
      });
      if (verifyError || !verifyData?.success) {
        throw new Error(verifyData?.error || verifyError?.message || 'Verification failed');
      }
      if (!verifyData.verified) {
        setError('Invalid code. Please try again.');
        setDigits(Array(CODE_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
      } else {
        const { generateDeviceToken } = await import('../../lib/deviceTrust');
        const token = generateDeviceToken();
        onVerified(token);
      }
    } catch {
      setError('Verification failed. Please try again.');
      setDigits(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  }, [phone, onVerified]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const digit = value.slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);

    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (digit && index === CODE_LENGTH - 1) {
      const code = next.join('');
      if (code.length === CODE_LENGTH) {
        submitCode(code);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setDigits(next);
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();

    if (pasted.length === CODE_LENGTH) {
      submitCode(pasted);
    }
  };

  const handleResend = async () => {
    setError(null);
    setResendCooldown(RESEND_COOLDOWN);
    setSecondsLeft(TOTAL_SECONDS);
    setDigits(Array(CODE_LENGTH).fill(''));
    inputRefs.current[0]?.focus();
    try {
      await supabase.functions.invoke('verify-phone-send', { body: { phone } });
    } catch {
      setError('Failed to resend code. Please try again.');
    }
  };

  const expired = secondsLeft <= 0;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-bold text-[#0D1827]">Verify Your Identity</h2>
        <p className="mt-2 text-sm text-text-muted">
          We sent a 6-digit code to <span className="font-medium text-text-primary">{maskedPhone}</span>
        </p>
      </div>

      {error && (
        <div className="p-3 text-sm text-status-danger bg-status-danger-light border border-status-danger/20 rounded-lg">
          {error}
        </div>
      )}

      {expired ? (
        <div className="text-center space-y-4">
          <p className="text-sm text-status-danger font-medium">Code expired</p>
          <button
            type="button"
            onClick={handleResend}
            className="text-sm font-medium text-accent-blue hover:text-accent-blue-hover hover:underline transition-colors"
          >
            Send a new code
          </button>
        </div>
      ) : (
        <>
          <div className="flex justify-center gap-2" onPaste={handlePaste}>
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                disabled={verifying}
                className="w-12 h-14 text-center text-xl font-semibold border border-surface-border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-blue-wash focus:border-accent-blue transition-colors disabled:opacity-50"
                aria-label={`Digit ${i + 1}`}
              />
            ))}
          </div>

          <p className="text-center text-sm text-text-muted">
            Code expires in <span className="font-medium">{formatTime(secondsLeft)}</span>
          </p>

          <div className="text-center">
            {resendCooldown > 0 ? (
              <p className="text-sm text-text-muted">
                Resend available in {resendCooldown}s
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                className="text-sm font-medium text-accent-blue hover:text-accent-blue-hover hover:underline transition-colors"
              >
                Resend code
              </button>
            )}
          </div>
        </>
      )}

      <div className="flex items-center justify-center gap-4 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={verifying}
          className="text-sm font-medium text-text-muted hover:text-text-primary hover:underline transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        {!expired && (
          <button
            type="button"
            onClick={() => {
              const code = digits.join('');
              if (code.length === CODE_LENGTH) submitCode(code);
            }}
            disabled={verifying || digits.join('').length < CODE_LENGTH}
            className="px-6 py-2.5 min-h-[44px] text-sm font-semibold text-white bg-[#2563EB] rounded-lg shadow-sm hover:bg-accent-blue-hover hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/25 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-blue disabled:opacity-50 transition-all duration-200"
          >
            {verifying ? 'Verifying...' : 'Verify'}
          </button>
        )}
      </div>
    </div>
  );
};

export default SMSVerification;
